import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  useToast,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Spinner,
} from "@chakra-ui/react";
import { cqAPI, getBid } from "../../lib/Auth";
import { useNavigate } from "react-router-dom";
import CustomizeAIAssistantInstruction from "./CustomizeAIAssistantInstruction";
import CustomizeAIDetail from "./CustomizeAIDetail";
import HighlightedTextBox from "./HighlightedTextBox";

const STORAGE_KEY_PREFIX = 'ai_instructions_draft-';

const CustomizeAIAssistant = () => {
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const [instructions, setInstructions] = useState(() => {
    try {
      const draft = localStorage.getItem(STORAGE_KEY_PREFIX + getBid());
      return draft ? JSON.parse(draft) : "";
    } catch {
      return "";
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newOrganizedInstructions, setNewOrganizedInstructions] = useState({});
  const [preOrganizedDraft, setPreOrganizedDraft] = useState("");
  const toast = useToast();
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: draftIsOpen, onOpen: draftOnOpen, onClose: draftOnClose } = useDisclosure()

  const lastSavedRef = useRef(null);
  const json = useMemo(() => {
    try {
      return JSON.stringify(instructions);
    } catch {
      return "";
    }
  }, [instructions]);

  const saveNow = () => {
    if (!json || json === lastSavedRef.current) return;
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + getBid(), json);
      lastSavedRef.current = json;
    } catch {
      console.error("Failed to save Drafts.");
    }
  };

  useEffect(() => {
    const id = setInterval(saveNow, 10_000);
    return () => clearInterval(id);
  }, [json]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + getBid(), JSON.stringify(instructions));
    } catch {
      console.error("Failed to save Drafts.");
    }
  }, [instructions]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const res = await cqAPI.post("/settings/ai", {
        instructions: instructions,
      });
      if (res.type === "ok") {
        setNewOrganizedInstructions(res?.data?.organized_instructions || {});
        setPreOrganizedDraft(res?.data?.draft_response_pre_prompt || "");
        draftOnOpen()
        setIsSubmitting(false);
        setInstructions("");
        saveNow();
      } else {
        toast({
          title: "Error",
          description: res.msg || "Failed to save.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (e) {
      draftOnOpen()
      setIsSubmitting(false);
      toast({
        title: "Error",
        description: e?.msg || "Unexpected error while configuring.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box my={8} px={4} width="100%" height="100%" overflowY="auto">
      <Box p={1} bgColor={bgColor} borderRadius="md" width="full">
        <Flex minH="60px" flexDirection="row">
          <Flex justifyContent="flex-start" flexDirection="column" alignItems='start'>
            <Text align="start" fontSize="sm" fontWeight="bold" ml={4} mt={2}>
              AI Setup Assistant
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" gap={2} ml='auto' mr={4} minHeight="20px" minWidth="20px" align="center">
            <Button
              isDisabled={isSubmitting}
              variant="ghost"
              onClick={() => {
                saveNow();
                navigate(`/settings/customize-ai?bid=${getBid()}`);
              }}>
              Close
            </Button>
            <Button
              isDisabled={!instructions.trim() || isSubmitting}
              onClick={async () => {
                saveNow();
                onOpen();
              }}
            >
              {isSubmitting ? <Spinner/> : "Submit"}
            </Button>
          </Flex>
        </Flex>
      </Box>
      <Box mt={4} p={1} bgColor={bgColor} borderRadius="md" width="full">
        <Flex align="center" minH="90px" flexDirection="column" alignItems="start">
          <CustomizeAIAssistantInstruction instructions={instructions} inEditMode={!isSubmitting} onChange={setInstructions}/>
        </Flex>
      </Box>
      <Modal onClose={onClose} isOpen={isOpen} size="lg">
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Configure AI Settings</ModalHeader>
          <ModalCloseButton/>
          <ModalBody minHeight="150px">
            <Text fontWeight="bold">
              Have you finished entering your instructions?
            </Text>
            Once you submit, they can&apos;t be edited and any existing AI settings will be overwritten.
            <br/>
            <br/>
            You can still view past instructions in Setup History under the AI Setup Assistant.
            <br/>
            <br/>
          </ModalBody>
          <ModalFooter mr={4}>
            <Button variant="ghost" onClick={onClose} mr={2}>Go back</Button>
            <Button
              isDisabled={!instructions.trim() || isSubmitting}
              onClick={async () => {
                onClose();
                await handleSubmit();
              }} minWidth="80px">
              {isSubmitting ? <Spinner/> : "Submit"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal onClose={draftOnClose} isOpen={draftIsOpen} size="5xl" closeOnOverlayClick={false}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>AI settings have been updated</ModalHeader>
          <ModalBody>
            <Text fontWeight="bold">
              Preview your changes
            </Text>
            To make further changes, run the AI setup assistant again
            <br/>
            <Box px={1} width="full" >
            <HighlightedTextBox input={preOrganizedDraft}/>
            </Box>
            <br/>
            <CustomizeAIDetail organized_instructions={newOrganizedInstructions} PreviewMode={true}/>
          </ModalBody>
          <ModalFooter mr={4}>
            <Button
              onClick={async () => {
                draftOnClose()
                navigate(`/settings/customize-ai?bid=${getBid()}`)
              }} minWidth="80px">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
};

export { CustomizeAIAssistant, STORAGE_KEY_PREFIX };
